import { useState, useEffect } from 'react';
import { incidentsApiService } from '../services/incidentsApi';
import { Incident } from '../types/incident';
import { X, Loader, Search, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';
import { EmptyState } from './ui/EmptyState';
import { SkeletonLoader } from './SkeletonLoader';

interface LinkIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  itemId: string;
  itemType: 'note' | 'warning' | 'ban';
}

export const LinkIncidentModal = ({ isOpen, onClose, onSuccess, itemId, itemType }: LinkIncidentModalProps) => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLinking, setIsLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (isOpen) {
      fetchIncidents();
    }
  }, [isOpen, debouncedSearch]);

  const fetchIncidents = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await incidentsApiService.getAllIncidents(1, 50);
      setIncidents(response.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (incidentId: string) => {
    setIsLinking(incidentId);
    setError(null);

    try {
      await incidentsApiService.linkItem(incidentId, itemId, itemType);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link incident');
    } finally {
      setIsLinking(null);
    }
  };

  const filteredIncidents = incidents.filter(incident =>
    incident.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    incident.id.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-2xl max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-incident-title"
        >
          <div className="modal-header">
            <h2 id="link-incident-title" className="modal-title">Link to Incident</h2>
            <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input w-full pl-9"
              placeholder="Search incidents..."
              aria-label="Search incidents"
            />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="space-y-2" aria-busy="true">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="subcard p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader className="h-3.5 w-1/2" />
                      <SkeletonLoader className="h-3 w-1/3" />
                    </div>
                    <SkeletonLoader className="h-7 w-14 rounded-md" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredIncidents.length === 0 ? (
                  <EmptyState
                    bare
                    compact
                    icon={Search}
                    title="No incidents found"
                    description={debouncedSearch ? 'Try a different title or incident ID.' : 'Create an incident first, then link this item to it.'}
                  />
                ) : (
                  filteredIncidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="subcard p-3 flex items-center justify-between gap-3 hover:border-glass-200/25 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-white truncate">{incident.title}</h3>
                        <p className="text-[11px] text-glass-400 font-mono truncate">{incident.id}</p>
                      </div>
                      <button
                        onClick={() => handleLink(incident.id)}
                        disabled={isLinking === incident.id}
                        className="glass-button-sm shrink-0"
                      >
                        {isLinking === incident.id ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Linking...
                          </>
                        ) : (
                          'Link'
                        )}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
